import type { TFunction } from "i18next";

/**
 * BUG-691: coded WARNINGS of a route build that completed — the build is fine to
 * use, but something about it is not what the dispatcher would assume.
 *
 * Today the only code is `PLANNING_MATRIX_ESTIMATED` {route_count}: the road
 * routing service (OSRM) was unreachable, so the build fell back to a
 * straight-line estimate — routes exist, but their times and kilometres are
 * approximate. Each such route also carries `eta_estimated: true` in the build
 * result. The warnings ride in the poller's `result.warnings` and in the
 * `routes_built` notification's `extra_data.warnings`.
 */

export interface BuildWarning {
  code: string;
  params: Record<string, unknown> | null;
}

export const MATRIX_ESTIMATED = "PLANNING_MATRIX_ESTIMATED";

/** Reads a `warnings` array off an untyped payload, dropping malformed entries. */
export function parseBuildWarnings(raw: unknown): BuildWarning[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    if (typeof entry !== "object" || entry === null) return [];
    const record = entry as Record<string, unknown>;
    if (typeof record.code !== "string" || record.code.trim() === "") return [];
    const params =
      typeof record.params === "object" &&
      record.params !== null &&
      !Array.isArray(record.params)
        ? (record.params as Record<string, unknown>)
        : null;
    return [{ code: record.code.trim(), params }];
  });
}

/**
 * One warning as a sentence the dispatcher can act on. A code this bundle does not
 * know is named honestly («Предупреждение сервера: <code>») — never dropped, never
 * a raw key path.
 */
export function describeBuildWarning(
  warning: BuildWarning,
  t: TFunction,
): string {
  if (warning.code === MATRIX_ESTIMATED) {
    const count = Number(warning.params?.route_count);
    return Number.isInteger(count) && count > 0
      ? t("routes.buildWarnings.matrixEstimated", { count })
      : t("routes.buildWarnings.matrixEstimatedNoCount");
  }
  return t("routes.buildWarnings.unknown", { code: warning.code });
}

/** Every warning as a sentence, in payload order. */
export function describeBuildWarnings(
  warnings: readonly BuildWarning[] | null | undefined,
  t: TFunction,
): string[] {
  return (warnings ?? []).map((warning) => describeBuildWarning(warning, t));
}
